import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './CookieBanner.css';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieBannerProps {
  onConsentChange: (consent: CookieConsent) => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onConsentChange }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always true - required for site functionality
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const hasNotifiedParent = useRef(false);

  // Add reset function for testing
  const resetCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentDate');
    hasNotifiedParent.current = false; // Reset notification flag
    setIsVisible(true);
  };

  // Expose reset function to window for testing in development
  useEffect(() => {
    // Check if we're in development mode - safer for tests
    const isDev = process.env.NODE_ENV === 'development' ||
      (typeof window !== 'undefined' &&
        (window as any).import?.meta?.env?.DEV);

    if (isDev) {
      (window as any).resetCookieConsent = resetCookieConsent;
    }
  }, []);

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookieConsent');
    if (!savedConsent) {
      setIsVisible(true);
    } else {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
        // Only notify parent once on initial load
        if (!hasNotifiedParent.current) {
          onConsentChange(parsedConsent);
          hasNotifiedParent.current = true;
        }
      } catch (error) {
        console.error('Error parsing saved cookie consent:', error);
        localStorage.removeItem('cookieConsent');
        setIsVisible(true);
      }
    }
  }, [onConsentChange]);

  const handleAcceptAll = () => {
    const allConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    saveConsent(allConsent);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    saveConsent(necessaryOnly);
  };

  const handleCustomConsent = () => {
    saveConsent(consent);
  };

  const saveConsent = (consentData: CookieConsent) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setConsent(consentData);
    onConsentChange(consentData);
    hasNotifiedParent.current = true; // Mark that we've notified parent
    setIsVisible(false);
  };

  const handleConsentChange = (type: keyof CookieConsent, value: boolean) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setConsent(prev => ({ ...prev, [type]: value }));
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-banner">
      <div className="cookie-banner-overlay" />
      <div className="cookie-banner-content">
        <div className="cookie-banner-header">
          <h3>{t('cookies.title')}</h3>
          <p>{t('cookies.description')}</p>
        </div>

        {showDetails && (
          <div className="cookie-details">
            <div className="cookie-category">
              <div className="cookie-category-header">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={consent.necessary}
                    disabled={true}
                    readOnly
                  />
                  <span className="toggle-slider"></span>
                  <span className="cookie-category-title">{t('cookies.necessary.title')}</span>
                </label>
              </div>
              <p className="cookie-category-description">{t('cookies.necessary.description')}</p>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-header">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={consent.analytics}
                    onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="cookie-category-title">{t('cookies.analytics.title')}</span>
                </label>
              </div>
              <p className="cookie-category-description">{t('cookies.analytics.description')}</p>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-header">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={consent.marketing}
                    onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="cookie-category-title">{t('cookies.marketing.title')}</span>
                </label>
              </div>
              <p className="cookie-category-description">{t('cookies.marketing.description')}</p>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-header">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={consent.preferences}
                    onChange={(e) => handleConsentChange('preferences', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="cookie-category-title">{t('cookies.preferences.title')}</span>
                </label>
              </div>
              <p className="cookie-category-description">{t('cookies.preferences.description')}</p>
            </div>
          </div>
        )}

        <div className="cookie-banner-actions">
          {!showDetails ? (
            <>
              <button
                className="cookie-btn cookie-btn-accept-all"
                onClick={handleAcceptAll}
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                className="cookie-btn cookie-btn-necessary"
                onClick={handleAcceptNecessary}
              >
                {t('cookies.necessaryOnly')}
              </button>
              <button
                className="cookie-btn cookie-btn-settings"
                onClick={() => setShowDetails(true)}
              >
                {t('cookies.settings')}
              </button>
            </>
          ) : (
            <>
              <button
                className="cookie-btn cookie-btn-accept-all"
                onClick={handleAcceptAll}
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                className="cookie-btn cookie-btn-save"
                onClick={handleCustomConsent}
              >
                {t('cookies.savePreferences')}
              </button>
              <button
                className="cookie-btn cookie-btn-back"
                onClick={() => setShowDetails(false)}
              >
                {t('cookies.back')}
              </button>
            </>
          )}
        </div>

        <div className="cookie-banner-links">
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            {t('cookies.privacyPolicy')}
          </a>
          <span>|</span>
          <a href="/cookie-policy" target="_blank" rel="noopener noreferrer">
            {t('cookies.cookiePolicy')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;